import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_CORPORATE_SIDEBAR_SETTINGS,
  type CorporateSidebarSettings,
} from '../utils/corporateSectionSettings';

interface CorporateSidebarSettingsPanelProps {
  corporateNavDisplayMode?: 'panel' | 'dropdown';
  corporateSidebarLayoutMode?: 'below_hero' | 'below_navbar';
  corporateSidebarSettings?: CorporateSidebarSettings;
  onMenuSettingChange: (setting: string, value: unknown) => void;
}

const CorporateSidebarSettingsPanel = ({
  corporateNavDisplayMode = 'dropdown',
  corporateSidebarLayoutMode = 'below_hero',
  corporateSidebarSettings = {},
  onMenuSettingChange,
}: CorporateSidebarSettingsPanelProps) => {
  const { t } = useTranslation('salesSystem');

  const sidebarSettings = {
    ...DEFAULT_CORPORATE_SIDEBAR_SETTINGS,
    ...corporateSidebarSettings,
  };

  const updateSidebarSetting = (field: keyof typeof sidebarSettings, value: string) => {
    onMenuSettingChange('corporateSidebarSettings', {
      ...sidebarSettings,
      [field]: value,
    });
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
        {t('ecommerceCorporateNav.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('ecommerceCorporateNav.description')}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ fontSize: '0.9rem', mb: 0.5 }}>
              {t('ecommerceCorporateNav.displayModeTitle')}
            </FormLabel>
            <RadioGroup
              row
              value={corporateNavDisplayMode || 'dropdown'}
              onChange={(e) => onMenuSettingChange('corporateNavDisplayMode', e.target.value)}
            >
              <FormControlLabel
                value="dropdown"
                control={<Radio />}
                label={t('ecommerceCorporateNav.displayModeDropdown')}
              />
              <FormControlLabel
                value="panel"
                control={<Radio />}
                label={t('ecommerceCorporateNav.displayModePanel')}
              />
            </RadioGroup>
          </FormControl>
        </Grid>

        {corporateNavDisplayMode === 'panel' && (
          <>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontSize: '0.9rem', mb: 0.5 }}>
                  {t('ecommerceMenuSettings.corporateSidebarLayout.title')}
                </FormLabel>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {t('ecommerceMenuSettings.corporateSidebarLayout.help')}
                </Typography>
                <RadioGroup
                  row
                  value={corporateSidebarLayoutMode || 'below_hero'}
                  onChange={(e) =>
                    onMenuSettingChange(
                      'corporateSidebarLayoutMode',
                      e.target.value === 'below_navbar' ? 'below_navbar' : 'below_hero'
                    )
                  }
                >
                  <FormControlLabel
                    value="below_hero"
                    control={<Radio />}
                    label={t('ecommerceMenuSettings.corporateSidebarLayout.belowHero')}
                  />
                  <FormControlLabel
                    value="below_navbar"
                    control={<Radio />}
                    label={t('ecommerceMenuSettings.corporateSidebarLayout.belowNavbar')}
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                {t('ecommerceMenuSettings.corporateSidebarColors.title')}
              </Typography>
              <Grid container spacing={2}>
                {[
                  {
                    key: 'backgroundColor',
                    label: t('ecommerceMenuSettings.corporateSidebarColors.backgroundColor'),
                  },
                  { key: 'borderColor', label: t('ecommerceMenuSettings.corporateSidebarColors.borderColor') },
                  { key: 'labelColor', label: t('ecommerceMenuSettings.corporateSidebarColors.labelColor') },
                  { key: 'itemColor', label: t('ecommerceMenuSettings.corporateSidebarColors.itemColor') },
                  {
                    key: 'itemHoverBackgroundColor',
                    label: t('ecommerceMenuSettings.corporateSidebarColors.itemHoverBackgroundColor'),
                  },
                  {
                    key: 'itemHoverColor',
                    label: t('ecommerceMenuSettings.corporateSidebarColors.itemHoverColor'),
                  },
                  {
                    key: 'activeBackgroundColor',
                    label: t('ecommerceMenuSettings.corporateSidebarColors.activeBackgroundColor'),
                  },
                  {
                    key: 'activeTextColor',
                    label: t('ecommerceMenuSettings.corporateSidebarColors.activeTextColor'),
                  },
                  { key: 'subitemColor', label: t('ecommerceMenuSettings.corporateSidebarColors.subitemColor') },
                  {
                    key: 'subitemActiveColor',
                    label: t('ecommerceMenuSettings.corporateSidebarColors.subitemActiveColor'),
                  },
                  {
                    key: 'subitemActiveBackgroundColor',
                    label: t('ecommerceMenuSettings.corporateSidebarColors.subitemActiveBackgroundColor'),
                  },
                ].map(({ key, label }) => (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <TextField
                      fullWidth
                      size="small"
                      label={label}
                      value={sidebarSettings[key as keyof typeof sidebarSettings] || ''}
                      onChange={(e) =>
                        updateSidebarSetting(key as keyof typeof sidebarSettings, e.target.value)
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Box
                              component="input"
                              type="color"
                              value={
                                String(sidebarSettings[key as keyof typeof sidebarSettings] || '#ffffff').startsWith(
                                  '#'
                                )
                                  ? String(sidebarSettings[key as keyof typeof sidebarSettings])
                                  : '#ffffff'
                              }
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                updateSidebarSetting(key as keyof typeof sidebarSettings, e.target.value)
                              }
                              sx={{
                                border: 'none',
                                width: 24,
                                height: 24,
                                p: 0,
                                background: 'none',
                                cursor: 'pointer',
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </>
        )}
      </Grid>
    </Paper>
  );
};

export default CorporateSidebarSettingsPanel;
